import { useCallback, useState } from 'react';
import { getPresignedUrlOnly, uploadFile } from '@/lib/api/fileUpload';

export interface FileState {
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
    url?: string;
    abortController?: AbortController;
}

export interface UseFileUploadProps {
    objectType?: string;
    onProgress?: (payload: { name: string; progress: number }) => void;
    onUploaded?: (payload: { name: string; src: string }) => void;
    onError?: (error: Error) => void;
}

export interface UseFileUploadReturn {
    fileStates: FileState[];
    errorMessage: string | null;
    uploadSingleFile: (file: File, idx: number) => Promise<void>;
    uploadAllFiles: () => Promise<void>;
    deleteFile: (idx: number) => void;
    clearFiles: () => void;
    setFiles: (files: File[]) => void;
    addFiles: (files: File[]) => void;
    setErrorMessage: (message: string | null) => void;
}

function formatErrorMessage(error: Error): string {
    if (error.message) {
        return error.message;
    }
    return 'Please reach out to us at friends@stanwith.me.';
}

export const useFileUpload = ({
    objectType = 'unknown',
    onProgress,
    onUploaded,
    onError,
}: UseFileUploadProps = {}): UseFileUploadReturn => {
    const [fileStates, setFileStates] = useState<FileState[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const uploadSingleFile = useCallback(
        async (file: File, idx: number) => {
            const controller = new AbortController();
            setFileStates(prev =>
                prev.map((currentFile, i) =>
                    i === idx
                        ? {
                              ...currentFile,
                              status: 'uploading',
                              progress: 0,
                              error: undefined,
                              abortController: controller,
                          }
                        : currentFile
                )
            );
            setErrorMessage(null);

            try {
                const presignedUrl = await getPresignedUrlOnly(file, objectType);
                const response = await uploadFile(file, {
                    presignedData: {
                        url: presignedUrl.url,
                        fields: presignedUrl.fields,
                        key: presignedUrl.objectKey,
                        response: presignedUrl.response,
                    },
                    onProgress: progress => {
                        setFileStates(prev =>
                            prev.map((currentFile, i) =>
                                i === idx ? { ...currentFile, progress, status: 'uploading' } : currentFile
                            )
                        );
                        onProgress?.({ name: file.name, progress });
                    },
                    signal: controller.signal,
                });

                setFileStates(prev =>
                    prev.map((currentFile, i) =>
                        i === idx
                            ? {
                                  ...currentFile,
                                  progress: 100,
                                  status: 'success',
                                  url: response.url,
                                  abortController: undefined,
                              }
                            : currentFile
                    )
                );
                onUploaded?.({ name: file.name, src: response.url });
            } catch (error) {
                if ((error as Error)?.name === 'AbortError' || controller.signal.aborted) {
                    // Remove the file from state if aborted
                    setFileStates(prev => prev.filter((_, i) => i !== idx));
                    return;
                }
                const msg = formatErrorMessage(error as Error);
                setFileStates(prev =>
                    prev.map((currentFile, i) =>
                        i === idx
                            ? { ...currentFile, status: 'error', error: msg, abortController: undefined }
                            : currentFile
                    )
                );
                onError?.(error as Error);
            }
        },
        [objectType, onProgress, onUploaded, onError]
    );

    const uploadAllFiles = useCallback(async () => {
        await Promise.allSettled(
            fileStates.map((currentFile, idx) =>
                currentFile.status === 'pending' ? uploadSingleFile(currentFile.file, idx) : Promise.resolve()
            )
        );
    }, [fileStates, uploadSingleFile]);

    const deleteFile = (idx: number) => {
        setFileStates(prev => {
            const fileState = prev[idx];
            if (fileState?.status === 'uploading' && fileState.abortController) {
                fileState.abortController.abort();
                return prev;
            }
            // Remove file from state if not uploading
            return prev.filter((_, i) => i !== idx);
        });
    };

    const clearFiles = () => {
        setFileStates([]);
        setErrorMessage(null);
    };

    const setFiles = (files: File[]) => {
        setErrorMessage(null);
        setFileStates(files.map(file => ({ file, progress: 0, status: 'pending' })));
    };

    const addFiles = (files: File[]) => {
        setErrorMessage(null);
        setFileStates(prev => {
            const existingFiles = new Set(prev.map(f => f.file.name + f.file.lastModified));
            const newFiles = files.filter(file => !existingFiles.has(file.name + file.lastModified));
            return [...prev, ...newFiles.map(file => ({ file, progress: 0, status: 'pending' as const }))];
        });
    };

    return {
        fileStates,
        errorMessage,
        uploadSingleFile,
        uploadAllFiles,
        deleteFile,
        clearFiles,
        setFiles,
        addFiles,
        setErrorMessage,
    };
};
